import { format, isValid, parseISO } from 'date-fns';

/**
 * Display helpers for Ask citations (Slice 3d). Citations are non-interactive:
 * these helpers only turn raw text-tree paths into calm human labels.
 */

export interface CitationLabel {
  /** Human-readable label, or the raw path when the shape is unrecognized. */
  text: string;
  /** True when `text` is a raw path and should render in monospace. */
  mono: boolean;
}

const JOURNAL_PATH = /^journal\/([^/]+)\.md$/;
const LIST_PATH = /^lists\/([^/]+)\.md$/;
const NOTE_PATH = /^notes\/([^/]+)\.md$/;

/** Turns a slug stem like `weekend-plans` into `weekend plans`. */
function slugToWords(stem: string): string {
  return stem.replace(/-/g, ' ');
}

/**
 * Maps a citation path to its display label:
 * - `journal/2026-07-06.md` → "Journal — July 6, 2026" (filename stem on parse failure)
 * - `lists/groceries.md`    → "List — groceries"
 * - `notes/summer-trip.md`  → "Note — summer trip"
 * - anything else           → the raw path, flagged `mono` for monospace rendering.
 */
export function citationLabel(path: string): CitationLabel {
  const journal = JOURNAL_PATH.exec(path);
  if (journal) {
    const stem = journal[1];
    const parsed = parseISO(stem);
    const day = isValid(parsed) ? format(parsed, 'MMMM d, yyyy') : stem;
    return { text: `Journal — ${day}`, mono: false };
  }

  const list = LIST_PATH.exec(path);
  if (list) {
    return { text: `List — ${slugToWords(list[1])}`, mono: false };
  }

  const note = NOTE_PATH.exec(path);
  if (note) {
    return { text: `Note — ${slugToWords(note[1])}`, mono: false };
  }

  return { text: path, mono: true };
}

/** Removes duplicate citations by `path` + `line`, keeping first occurrence order. */
export function dedupeCitations<T extends { path: string; line?: number | null }>(
  citations: readonly T[]
): T[] {
  const seen = new Set<string>();
  const result: T[] = [];
  for (const citation of citations) {
    const key = `${citation.path}#${citation.line ?? ''}`;
    if (!seen.has(key)) {
      seen.add(key);
      result.push(citation);
    }
  }
  return result;
}
